import { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import {
  apiGetCart,
  apiAddToCart,
  apiUpdateCartItem,
  apiRemoveCartItem,
  apiClearCart,
} from '../api';
import logger from '../utils/logger';
import { getStockIssue } from '../utils/stock';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CART':
      return { ...state, items: action.payload };
    case 'ADD_TO_CART': {
      const existing = state.items.find((item) => item.id === action.payload.book.id);
      const addQty = action.payload.qty || 1;
      if (existing) {
        return {
          ...state,
          items: state.items.map((item) =>
            item.id === action.payload.book.id ? { ...item, quantity: item.quantity + addQty } : item
          ),
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload.book, quantity: addQty }],
      };
    }
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
      };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id
            ? { ...item, quantity: Math.max(1, action.payload.quantity) }
            : item
        ),
      };
    case 'CLEAR_CART':
      return { ...state, items: [] };
    default:
      return state;
  }
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  const itemsRef = useRef(state.items);

  useEffect(() => {
    itemsRef.current = state.items;
  }, [state.items]);

  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem('bookscape_token');
    if (!token) {
      dispatch({ type: 'SET_CART', payload: [] });
      return [];
    }
    try {
      const data = await apiGetCart();
      const items = (data.cart || []).map((item) => ({
        id: item.book.id,
        stock: item.book.stock,
        isActive: item.book.isActive,
        cartItemId: item.id,
        title: item.book.title,
        author: item.book.author,
        image: item.book.image,
        price: item.book.price,
        originalPrice: item.book.originalPrice,
        discount: item.book.discount,
        usdPrice: item.book.usdPrice,
        usdOriginalPrice: item.book.usdOriginalPrice,
        usdDiscount: item.book.usdDiscount,
        taxRate: item.book.taxRate,
        taxInclusive: item.book.taxInclusive,
        taxLabel: item.book.taxLabel,
        isPreorder: item.book.isPreorder,
        preorderReleaseDate: item.book.preorderReleaseDate,
        preorderMessage: item.book.preorderMessage,
        quantity: item.quantity,
      }));
      dispatch({ type: 'SET_CART', payload: items });
      return items;
    } catch (e) {
      logger.error('Failed to fetch cart', e);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  useEffect(() => {
    const syncAfterAuthChange = async (event) => {
      if (!event.detail?.authenticated) {
        dispatch({ type: 'CLEAR_CART' });
        return;
      }
      const guestItems = itemsRef.current.filter((item) => !item.cartItemId);
      try {
        await Promise.all(
          guestItems.map((item) => apiAddToCart(item.id, item.quantity || 1))
        );
      } catch (error) {
        logger.error('Failed to merge guest cart after login', error);
      } finally {
        await fetchCart();
      }
    };
    window.addEventListener('bookscape:auth-changed', syncAfterAuthChange);
    return () => window.removeEventListener('bookscape:auth-changed', syncAfterAuthChange);
  }, [fetchCart]);

  const addToCart = async (book, qty = 1) => {
    const existing = itemsRef.current.find((item) => item.id === book.id);
    const quantity = Number(existing?.quantity || 0) + Number(qty);
    if (getStockIssue({ ...book, quantity })) return false;
    dispatch({ type: 'ADD_TO_CART', payload: { book, qty: Number(qty) } });
    const token = localStorage.getItem('bookscape_token');
    if (token) {
      try {
        await apiAddToCart(book.id, qty);
        fetchCart();
      } catch (e) {
        logger.error('Failed to add to cart API', e);
        await fetchCart();
      }
    }
  };

  const removeFromCart = async (id) => {
    const item = state.items.find((i) => i.id === id);
    dispatch({ type: 'REMOVE_FROM_CART', payload: id });
    const token = localStorage.getItem('bookscape_token');
    if (token && item?.cartItemId) {
      try {
        await apiRemoveCartItem(item.cartItemId);
      } catch (e) {
        logger.error('Failed to remove from cart API', e);
      }
    }
  };

  const updateQuantity = async (id, quantity) => {
    const item = state.items.find((i) => i.id === id);
    if (!item || getStockIssue({ ...item, quantity })) return false;
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id, quantity: Number(quantity) } });
    const token = localStorage.getItem('bookscape_token');
    if (token && item?.cartItemId) {
      try {
        await apiUpdateCartItem(item.cartItemId, quantity);
      } catch (e) {
        logger.error('Failed to update cart API', e);
        await fetchCart();
      }
    }
  };

  const clearCart = async () => {
    dispatch({ type: 'CLEAR_CART' });
    const token = localStorage.getItem('bookscape_token');
    if (token) {
      try {
        await apiClearCart();
      } catch (e) {
        logger.error('Failed to clear cart API', e);
      }
    }
  };

  const cartTotal = state.items.reduce((total, item) => total + item.price * item.quantity, 0);
  const cartCount = state.items.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart: state.items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
        cartCount,
        fetchCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
